import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EpikriseMarkupField } from '@/components/referrals/EpikriseMarkupField';
import {
  EPIKRISE_RECOMMENDATION_MAX,
  EPIKRISE_SECTIONS,
  EPIKRISE_TEXT01_LIMIT,
  EPIKRISE_TEXT_WARN_SHARE,
  MARKERING_OPTIONS,
  MARKERING_QUESTION,
  RECOMMENDATION_HEADING,
  epikriseHasMarkering,
  epikriseTextTotal,
  markupLength,
  type EpikriseForm,
  type EpikriseIssue,
  type Markering,
} from '@/lib/referral/epikrise';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { CHIP, FIELD, HEADING, SUB } from '@/components/referrals/epikrisePanelShared';

/**
 * Markering + "Anbefaling om opfølgning hos egen læge" (DIS01-S 4.10–4.16,
 * DIS02/03-S 4.11–4.18) and the VEJ 10036 template with guide texts
 * (DIS01-S 4.17/4.18, DIS02/03-S 4.14/4.15) with a live Text01 counter.
 */
export function EpikriseTextFields({
  form,
  update,
  attempted,
  issues,
}: {
  form: EpikriseForm;
  update: (patch: Partial<EpikriseForm>) => void;
  attempted: boolean;
  issues: EpikriseIssue[];
}) {
  const { t } = useTranslation();
  const [guideOpen, setGuideOpen] = useState<string | null>(null);
  const option = MARKERING_OPTIONS.find((o) => o.value === form.markering) ?? MARKERING_OPTIONS[0];
  const textTotal = epikriseTextTotal(form);
  const textLimit = EPIKRISE_TEXT01_LIMIT[form.kind];
  const recommendationLength = markupLength(form.recommendation.replace(/\s+$/, ''));

  const colourName = (colour: 'red' | 'yellow' | 'green' | 'white') =>
    colour === 'white'
      ? t('referrals.epikrise_markering_none', 'Ingen markering')
      : colour === 'red'
        ? t('referrals.epikrise_markering_red', 'Rød')
        : colour === 'yellow'
          ? t('referrals.epikrise_markering_yellow', 'Gul')
          : t('referrals.epikrise_markering_green', 'Grøn');

  return (
    <>
      {epikriseHasMarkering(form.kind) ? (
        <div className={SUB} data-testid="epikrise-markering">
          <label htmlFor="epikrise-markering" className={cn('block', FORM_LABEL_TYPE)}>
            {MARKERING_QUESTION}
          </label>
          <div className="flex items-center gap-2">
            <span
              data-testid="epikrise-markering-chip"
              data-colour={option?.colour}
              title={option ? colourName(option.colour) : ''}
              className={cn('mt-1 inline-block h-6 w-6 shrink-0 rounded-full', option ? CHIP[option.colour] : '')}
            />
            <select
              id="epikrise-markering"
              value={form.markering}
              onChange={(e) => update({ markering: e.target.value as Markering })}
              className={cn(TYPE.inputValue, FIELD)}
            >
              {MARKERING_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.text}
                </option>
              ))}
            </select>
          </div>
          <ul className="space-y-1 text-xs text-ds-muted" data-testid="epikrise-markering-legend">
            {MARKERING_OPTIONS.map((o) => (
              <li key={o.value} className="flex items-start gap-2">
                <span className={cn('mt-0.5 inline-block h-3 w-3 shrink-0 rounded-full', CHIP[o.colour])} aria-hidden="true" />
                <span>
                  <span className="font-medium">{colourName(o.colour)}:</span> {o.text}
                </span>
              </li>
            ))}
          </ul>
          <label htmlFor="epikrise-recommendation" className={cn('block pt-2', FORM_LABEL_TYPE)}>
            {RECOMMENDATION_HEADING}
          </label>
          <EpikriseMarkupField
            id="epikrise-recommendation"
            value={form.recommendation}
            onChange={(recommendation) => update({ recommendation })}
            rows={4}
            disabled={form.markering === 'none'}
            invalid={attempted && (issues.includes('recommendation_missing') || issues.includes('recommendation_too_long'))}
            describedBy="epikrise-recommendation-count"
          />
          <p
            id="epikrise-recommendation-count"
            data-testid="epikrise-recommendation-count"
            className={cn(FORM_HINT_TYPE, recommendationLength > EPIKRISE_RECOMMENDATION_MAX && 'text-ds-red-ink ')}
          >
            {form.markering === 'none'
              ? t('referrals.epikrise_recommendation_inactive', 'Feltet er inaktivt ved "Ingen markering".')
              : t(
                  'referrals.epikrise_recommendation_count',
                  '{{count}} af højst {{max}} tegn (skal udfyldes, når der er valgt en markering)',
                  { count: recommendationLength, max: EPIKRISE_RECOMMENDATION_MAX },
                )}
          </p>
          {attempted && issues.includes('recommendation_missing') && (
            <p role="alert" className={FORM_ERROR_TYPE} data-testid="epikrise-recommendation-missing">
              {t(
                'referrals.epikrise_recommendation_missing',
                'Advarsel: Der er valgt en markering. Udfyld "Anbefaling om opfølgning hos egen læge", før epikrisen kan dannes.',
              )}
            </p>
          )}
          {attempted && issues.includes('recommendation_too_long') && (
            <p role="alert" className={FORM_ERROR_TYPE}>
              {t('referrals.epikrise_recommendation_too_long', 'Anbefalingen må højst være {{max}} tegn.', {
                max: EPIKRISE_RECOMMENDATION_MAX,
              })}
            </p>
          )}
        </div>
      ) : (
        <p className={FORM_HINT_TYPE}>
          {t(
            'referrals.epikrise_no_markering_xdis05',
            'En billeddiagnostisk epikrise (XDIS05) har ingen markering eller anbefaling (ikke i 2005-skemaet).',
          )}
        </p>
      )}

      <div className={SUB} data-testid="epikrise-sections">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className={HEADING}>{t('referrals.epikrise_text', 'Epikrisetekst (VEJ nr 10036 af 30/11/2018)')}</div>
          <span data-testid="epikrise-text-count" className={cn(FORM_HINT_TYPE, textTotal > textLimit && 'text-ds-red-ink ')}>
            {t('referrals.epikrise_text_count', '{{count}} af højst {{max}} tegn', { count: textTotal, max: textLimit })}
          </span>
        </div>
        {EPIKRISE_SECTIONS.map((s) => (
          <div key={s.id}>
            <div className="flex items-center justify-between gap-2">
              <label htmlFor={`epikrise-section-${s.id}`} className={cn('block', FORM_LABEL_TYPE)}>
                {s.heading}
              </label>
              <button
                type="button"
                className="text-xs text-ds-teal hover:underline"
                aria-expanded={guideOpen === s.id}
                data-testid={`epikrise-guide-toggle-${s.id}`}
                onClick={() => setGuideOpen((g) => (g === s.id ? null : s.id))}
              >
                {t('referrals.epikrise_guide', 'Vejledning (afsnit {{vej}})', { vej: s.vej })}
              </button>
            </div>
            {guideOpen === s.id && (
              <p className={cn('rounded bg-ds-teal-tint px-2 py-1 ', FORM_HINT_TYPE)} data-testid={`epikrise-guide-${s.id}`}>
                VEJ nr 10036, {s.vej}: {s.guide}
              </p>
            )}
            <EpikriseMarkupField
              id={`epikrise-section-${s.id}`}
              value={form.sections[s.id]}
              onChange={(text) => update({ sections: { ...form.sections, [s.id]: text } })}
              rows={s.id === 'resume' ? 6 : 3}
            />
          </div>
        ))}
        {(attempted || textTotal > textLimit) && issues.includes('text_too_long') ? (
          <p role="alert" className={FORM_ERROR_TYPE} data-testid="epikrise-text-limit-alert">
            {t('referrals.epikrise_text_over_limit', 'Advarsel: Epikriseteksten er {{count}} tegn. Der må højst indtastes {{max}} tegn; forkort teksten, før epikrisen kan dannes.', {
              count: textTotal,
              max: textLimit,
            })}
          </p>
        ) : (
          textTotal >= Math.floor(textLimit * EPIKRISE_TEXT_WARN_SHARE) && (
            <p role="status" className="text-sm text-ds-amber-ink" data-testid="epikrise-text-limit-warning">
              {t('referrals.epikrise_text_near_limit', 'Advarsel: Epikriseteksten nærmer sig grænsen på {{max}} tegn ({{left}} tegn tilbage).', {
                max: textLimit,
                left: textLimit - textTotal,
              })}
            </p>
          )
        )}
        {(issues.includes('text_markup') || issues.includes('recommendation_markup')) && (
          <p role="alert" className={FORM_ERROR_TYPE}>
            {t('referrals.epikrise_markup_problem', 'Fremhævning kan ikke indlejres, og hver [b], [i] og [u] skal afsluttes.')}
          </p>
        )}
        <p className={FORM_HINT_TYPE}>
          {t('referrals.epikrise_markup_hint', 'Linjeskift bevares. Markér tekst og brug B, I eller U for fed, kursiv eller understreget.')}
        </p>
      </div>
    </>
  );
}
